import { Box, Image, Theme } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { createUrlWithActivity } from "@outschool/routes";
import { measurePerformance } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { SaveButton } from "../Saving";
import ActivityShareMenu from "./ActivityShareMenu";
import PlayVideoOverlayButton from "./PlayVideoOverlayButton";

interface ActivityPhotoProps {
  activity: ClassDetailsQuery_ActivityFragment;
  imageUrl: string;
  imageWidth: string | number;
  imageHeight: string | number;
  containerHeight: string | number;
  facebookAppId?: string;
  isActivitySavedByCurrentUser?: boolean;
  isMobileCdp?: boolean;
}

export default function ActivityPhoto({
  activity,
  imageUrl,
  imageWidth,
  imageHeight,
  containerHeight,
  facebookAppId,
  isActivitySavedByCurrentUser,
  isMobileCdp = false,
}: ActivityPhotoProps) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ActivityPhoto"
  );
  const { currentUser } = useSession();

  return (
    <Box
      id="about"
      sx={(theme: Theme) => {
        return {
          position: "relative",
          height: containerHeight,
          borderRadius: 0,

          [theme.breakpoints.up("md")]: {
            borderRadius: "0.5em",
          },

          overflow: "hidden",
          // Remove whitespace under image
          "> img": {
            display: "block",
          },
        };
      }}
    >
      <Image<{ nextImage: { priority: boolean } }>
        // TODO don't do this - we have no idea if this component is used in CSR or SSR
        nextImage={{
          priority: true,
        }}
        onLoad={() => {
          measurePerformance("os-page-load", {
            available_section_count:
              activity?.upcomingAvailableSectionCount || 0,
            slug_id: activity?.slug_id,
            section_count: activity?.upcomingSectionCount,
            has_sections: Number(activity?.upcomingSectionCount) > 0,
            source: "image",
          });
        }}
        width={imageWidth}
        height={imageHeight}
        data-pin-url={createUrlWithActivity(activity)}
        data-pin-description={activity.title}
        data-test-id="class-photo"
        sx={{ width: "100%", height: "100%" }}
        src={imageUrl}
        alt={t("Class")}
      />

      <PlayVideoOverlayButton
        activity={activity}
        clickableAreaIsButtonOnly={isMobileCdp}
      />

      {isMobileCdp && (
        <Box flex sx={{ position: "absolute", top: 12, right: 12, gap: 12 }}>
          <ActivityShareMenu
            user={currentUser}
            activity={activity}
            facebookAppId={facebookAppId}
            isOverlayVariant={isMobileCdp}
          />
          <SaveButton
            variant="link"
            isSet={isActivitySavedByCurrentUser}
            activity={activity}
            hideLabelOnMobile
            disabled={false}
            sx={{ padding: "1em" }}
            isOverlayVariant={true}
          />
        </Box>
      )}
    </Box>
  );
}
